import { useState, useCallback, useRef, useEffect } from 'react';

interface RetryOptions {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
  onMaxAttemptsReached?: (error: any) => void;
}

interface RetryState {
  isRetrying: boolean;
  attemptCount: number;
  lastError: any;
  canRetry: boolean;
  nextRetryIn: number;
}

interface UseRetryReturn<T> {
  execute: (...args: any[]) => Promise<T>;
  retry: () => Promise<T>;
  reset: () => void;
  state: RetryState;
}

export const useRetry = <T>(
  asyncFunction: (...args: any[]) => Promise<T>,
  options: RetryOptions = {}
): UseRetryReturn<T> => {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    retryCondition = () => true,
    onRetry,
    onMaxAttemptsReached,
  } = options;

  const [state, setState] = useState<RetryState>({
    isRetrying: false,
    attemptCount: 0,
    lastError: null,
    canRetry: false,
    nextRetryIn: 0,
  });

  const lastArgsRef = useRef<any[]>([]);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const calculateDelay = useCallback((attempt: number): number => {
    const delay = Math.min(baseDelay * Math.pow(backoffFactor, attempt), maxDelay);
    // Add jitter to prevent thundering herd
    return delay + Math.random() * 1000;
  }, [baseDelay, backoffFactor, maxDelay]);

  const clearTimers = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback((delayMs: number) => {
    let remaining = Math.ceil(delayMs / 1000);
    setState(prev => ({ ...prev, nextRetryIn: remaining }));

    countdownIntervalRef.current = setInterval(() => {
      remaining -= 1;
      setState(prev => ({ ...prev, nextRetryIn: remaining }));
      
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current!);
        countdownIntervalRef.current = null;
      }
    }, 1000);
  }, []);

  const executeWithRetry = useCallback(async (...args: any[]): Promise<T> => {
    lastArgsRef.current = args;
    
    const attemptExecution = async (attempt: number): Promise<T> => {
      try {
        setState(prev => ({
          ...prev,
          isRetrying: attempt > 0,
          attemptCount: attempt + 1,
          lastError: null,
          canRetry: false,
          nextRetryIn: 0,
        }));

        const result = await asyncFunction(...args);
        
        // Success - reset state
        setState(prev => ({
          ...prev,
          isRetrying: false,
          lastError: null,
          canRetry: false,
          nextRetryIn: 0,
        }));

        return result;
      } catch (error) {
        const shouldRetry = attempt < maxAttempts - 1 && retryCondition(error);
        
        setState(prev => ({
          ...prev,
          isRetrying: false,
          lastError: error,
          canRetry: shouldRetry,
          nextRetryIn: 0,
        }));

        if (shouldRetry) {
          const delay = calculateDelay(attempt);
          onRetry?.(attempt + 1, error);
          
          startCountdown(delay);
          
          return new Promise<T>((resolve, reject) => {
            retryTimeoutRef.current = setTimeout(async () => {
              try {
                const result = await attemptExecution(attempt + 1);
                resolve(result);
              } catch (retryError) {
                reject(retryError);
              }
            }, delay);
          });
        } else {
          onMaxAttemptsReached?.(error);
          throw error;
        }
      }
    };

    return attemptExecution(0);
  }, [asyncFunction, maxAttempts, retryCondition, onRetry, onMaxAttemptsReached, calculateDelay, startCountdown]);

  const retry = useCallback(async (): Promise<T> => {
    if (!state.canRetry) {
      throw new Error('Cannot retry: either max attempts reached or no previous error');
    }
    
    clearTimers();
    return executeWithRetry(...lastArgsRef.current);
  }, [state.canRetry, executeWithRetry, clearTimers]);

  const reset = useCallback(() => {
    clearTimers();
    setState({
      isRetrying: false,
      attemptCount: 0,
      lastError: null,
      canRetry: false,
      nextRetryIn: 0,
    });
  }, [clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    execute: executeWithRetry,
    retry,
    reset,
    state,
  };
};

// Predefined retry conditions
export const retryConditions = {
  networkErrors: (error: any) => {
    if (!error) return false;
    
    const message = error.message?.toLowerCase() || '';
    const code = error.code?.toLowerCase() || '';
    const status = error.status || error.response?.status;
    
    // Network-related errors
    if (message.includes('network') || message.includes('fetch') || 
        code.includes('network') || code === 'econnaborted') {
      return true;
    }
    
    // Server errors (5xx)
    if (status >= 500) {
      return true;
    }
    
    // Timeout errors
    if (message.includes('timeout') || code.includes('timeout')) {
      return true;
    }
    
    return false;
  },
  
  serverErrors: (error: any) => {
    const status = error.status || error.response?.status;
    return status >= 500;
  },
  
  rateLimitErrors: (error: any) => {
    const status = error.status || error.response?.status;
    const message = error.message?.toLowerCase() || '';
    
    return status === 429 || message.includes('rate limit') || message.includes('too many requests');
  },
  
  temporaryErrors: (error: any) => {
    return retryConditions.networkErrors(error) || 
           retryConditions.serverErrors(error) || 
           retryConditions.rateLimitErrors(error);
  },
  
  never: () => false,
  always: () => true,
};

export default useRetry;